import { Fragment, useCallback, useEffect, useState } from 'react';
import Pagination from 'components/Pagination/Pagination';
import Table from 'components/Table/Table';
import TableSkeleton from 'components/Table/TableSkeleton/TableSkeleton';
import { selectAuthUser } from 'features/auth/authSlice';
import { setNavbarTitle } from 'features/navbar/navbarSlice';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { actions } from 'utils/helpers';
import { UNAUTHORIZED } from 'constants';
import { useDeleteQueueTierMutation, useGetQueueTiersQuery } from 'features/call_center/queueTiersApiSlice';

import { Globals } from 'utils/styleconfig';
import { RiArrowRightSLine } from 'react-icons/ri';
import cardStyles from 'styles/modules/Card.module.scss';
import utilityStyles from "styles/modules/Utility.module.scss";

const QueueTiers = ({ label, tierId }) => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const [errorDisplayed, setErrorDisplayed] = useState(false);

  const {
    data: queueTiers,
    isFetching: isLoading,
    isError,
    error,
  } = useGetQueueTiersQuery({ id: params.id, searchParams: searchParams.toString() });
  // console.log('queueTiers', queueTiers)

  const [deleteTier] = useDeleteQueueTierMutation();
  const authUser = useSelector(selectAuthUser);

  useEffect(() => {
    dispatch(setNavbarTitle('Queue Tiers'));
  }, [dispatch]);

  useEffect(() => {
    if (isError && !errorDisplayed) {
      toast.error(error?.message || 'Could not load queue tiers, try refreshing the page');
      setErrorDisplayed(true);
    }
  }, [isError, error, errorDisplayed]);

  const onItemClickHandler = (tierId) => {
    navigate(`/call_center/queues/${params.id}/tiers/${tierId}`);
  };

  const onDeleteBtnHandler = useCallback(
    async (id, callFn = false) => {
      if (authUser?.actions[label]?.includes(actions.delete)) {
        if (callFn) {
          await deleteTier({ queueId: params.id, tierId: id }).unwrap();
        }
        return '';
      }
      return UNAUTHORIZED;
    },
    [authUser?.actions, deleteTier, label, params.id]
  );

  let buttonEl = null;

  const onAddBtnHandler = useCallback(() => {
    if (authUser?.actions[label].includes(actions.create)) {
      navigate(`/call_center/queues/${params.id}/tiers/new`);
    }
  }, [authUser?.actions, label, navigate, params?.id]);

  if (authUser?.actions[label].includes(actions.create)) {
    buttonEl = (
      <button
        onClick={onAddBtnHandler}
        className={utilityStyles.outline__button}
      >
        <span>Add new Queue Tier</span>
        <RiArrowRightSLine color={Globals.colorDarkBlue} size={16} />
      </button>
    );
  }

  return (
    <div>
      <div className={cardStyles.menu}>
        <h3>Queue Tiers</h3>
        <div
          className={`${utilityStyles.flexRow} ${utilityStyles.itemsCenter}`}
        >
          {buttonEl}
        </div>
      </div>
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <Fragment>
          <Table
            data={queueTiers || []}
            onItemClickHandler={onItemClickHandler}
            onDelete={onDeleteBtnHandler}
          />
          <Pagination totalCount={queueTiers?.size } />
        </Fragment>
      )}
    </div>
  );
};

export default QueueTiers;
